import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Share2, ListMusic, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function PlaylistView() {
  const { code } = useParams();
  const [pl, setPl] = useState(null);
  const [error, setError] = useState(false);
  const { playQueue, current, playing } = usePlayer();

  useEffect(() => {
    api.get(`/playlists/${code}`).then((r) => setPl(r.data)).catch(() => setError(true));
  }, [code]);

  const share = async () => {
    const url = `${window.location.origin}/playlists/${code}`;
    if (navigator.share) {
      try { await navigator.share({ title: pl?.title, url }); return; } catch(_){}
    }
    try { await navigator.clipboard.writeText(url); toast.success("Playlist link copied"); }
    catch { toast.error("Could not copy link"); }
  };

  if (error) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Playlist not found.</h1>
      <Link to="/songs" className="btn-pill btn-primary mt-6 inline-flex text-sm">Browse songs</Link>
    </div>
  );
  if (!pl) return <div className="max-w-3xl mx-auto px-6 py-24 text-sm text-neutral-500">Loading playlist…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
      <Link to="/library" className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-neutral-900 mb-8 tracking-widest uppercase">
        <ArrowLeft size={14} strokeWidth={1.5} /> Library
      </Link>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="eyebrow mb-3 flex items-center gap-1.5">
            <ListMusic size={12} strokeWidth={1.5} /> Playlist · {pl.songs.length} songs
          </p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight font-light">{pl.title}</h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="playlist-play-all"
            onClick={() => playQueue(pl.songs)}
            className="btn-pill btn-primary"
          >
            <Play size={14} strokeWidth={1.5} /> Play all
          </button>
          <button data-testid="playlist-share" onClick={share} className="btn-pill btn-outline">
            <Share2 size={14} strokeWidth={1.5} /> Share
          </button>
        </div>
      </div>

      <div className="mt-12 rounded-2xl bg-white border border-[--sanctum-border] overflow-hidden">
        {pl.songs.map((s, i) => {
          const isCurrent = current && current.id === s.id;
          return (
            <Link
              key={s.id}
              to={`/songs/${s.id}`}
              data-testid={`playlist-track-${s.id}`}
              className={`flex items-center gap-4 p-4 hover:bg-neutral-50 transition-colors ${i > 0 ? "border-t border-neutral-100" : ""}`}
            >
              <span className="w-6 text-xs tabular-nums text-neutral-400">{String(i + 1).padStart(2, "0")}</span>
              <img src={s.cover_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <div className={`font-medium truncate ${isCurrent ? "text-neutral-900" : ""}`}>
                  {s.title}
                  {isCurrent && playing && <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-[--sanctum-accent]">Now playing</span>}
                </div>
                <div className="text-xs text-neutral-500 truncate">{s.artist} · {s.genre}</div>
              </div>
              <span className="text-xs text-neutral-400">${s.price_mp3.toFixed(2)}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
